import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CardDepth, MonoLogo, VisaLogo } from './CardDesign';
import { getStoredChatId } from './chatId';
import seededRandom from './seededRandom';
import { formatMoney } from './cardsStore';
import './CardPopup.css';

// тривалості й криві зняті із запису екрана monobank: картка летить майже
// рівномірно (≈20px за кадр) і лише наприкінці м'яко гальмує
const OPEN_MS = 300;
const CLOSE_MS = 260;
const OPEN_EASE = 'cubic-bezier(0.3, 0.3, 0.35, 1)';
const CLOSE_EASE = 'cubic-bezier(0.25, 0.45, 0.45, 1)';
// торець під карткою на головній зсунутий на стільки вниз (CardDesign.css)
const BACKDROP_SHIFT = 'translate(0px, 4px)';
const BACKDROP_HIDDEN = 'translate(0px, 0px)';
const DEFAULT_CARD_NUMBER = '4441000000003070';
// Колір низу градієнта попапа — ним фарбуємо <body>, бо зону під
// home-indicator (safe-area) iOS малює фоном body, а не fixed-оверлеєм
const BODY_BG = '#101b45';
const BODY_BG_BY_VARIANT = { white: '#2d3978', usd: '#1d4457', eur: '#692778' };

// Переворот картки — один плавний поворот на 180° (кадр за кадром), як у
// monobank: повільний старт, швидка середина, м'яке гальмування. Параметри
// зняті із запису екрана: перспектива ≈ 2.7 ширини картки, на ребрі картка
// відсувається вглиб на ≈ 0.42 ширини (ближній край майже не росте).
const FLIP_MS = 900;
const FLIP_CLOSE_MS = 400; // перед закриттям повертаємо лицем швидше
const FLIP_PERSPECTIVE = 2.7;
// Затиснули картку — копіюємо номер і показуємо зелену плашку зверху
const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE = 10; // px — далі вважаємо, що це скрол, а не затискання
const COPY_TOAST_MS = 2000;
const FLIP_DEPTH = 0.42;
const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

// CVV і термін дії — «згенеровані» для користувача, але стабільні
// (прив'язані до chatId), щоб не мінялись при кожному відкритті
function generateCardSecrets(seed) {
  const rand = seededRandom(`${seed}-card-secrets`);
  let cvv = '';
  for (let i = 0; i < 3; i++) cvv += Math.floor(rand() * 10);
  const month = String(1 + Math.floor(rand() * 12)).padStart(2, '0');
  const year = 27 + Math.floor(rand() * 4);
  return { cvv, expiry: `${month}/${year}` };
}

const GROUPS = [
  [
    { icon: '/card-popup-icons/card-apple.webp', title: 'Налаштування Apple Pay', sub: 'Обрати скін картки — у нас їх багато' },
    { icon: '/card-popup-icons/card-block.webp', title: 'Заблокувати картку', sub: 'Ви завжди зможете її розблокувати' },
    { icon: '/card-popup-icons/card-limit.webp', title: 'Змінити кредитний ліміт', sub: 'Поточний ліміт 0 ₴' },
  ],
  [
    { icon: '/card-popup-icons/card-subs.webp', title: 'Підписки та сервіси', sub: 'Керування списаннями з картки' },
    { icon: '/card-popup-icons/card-share.webp', title: 'Відкрити доступ до рахунку' },
  ],
  [
    { icon: '/card-popup-icons/card-create.webp', title: 'Випустити пластикову картку' },
    { icon: '/card-popup-icons/card-new.webp', title: 'Перевипустити картку' },
    { icon: '/card-popup-icons/card-levelup.webp', title: 'Підвищити статус картки' },
    { icon: '/card-popup-icons/card-pin.webp', title: 'Налаштування ПІН-коду' },
    { icon: '/card-popup-icons/card-atb.webp', title: 'Додаткові картки', sub: 'Картка mono x АТБ' },
  ],
  [
    { icon: '/card-popup-icons/card-details.webp', title: 'Реквізити картки', sub: 'Для поповнення за IBAN' },
    { icon: '/card-popup-icons/card-info.webp', title: 'Надіслати виписку за карткою' },
  ],
];

// Остання група лише в додаткових картках (основну закрити не можна)
const CLOSE_CARD_GROUP = [
  { icon: '/card-popup-icons/card-close.svg', title: 'Закрити картку', action: 'close-card' },
];

// Пункти лише для основної (кредитної) картки
const MAIN_ONLY_ROWS = ['Змінити кредитний ліміт', 'Додаткові картки'];
// Інші підписи пунктів за дизайном картки (зі скріншотів monobank)
const ROW_OVERRIDES_BY_VARIANT = {
  white: { 'Налаштування Apple Pay': { title: 'Додати картку до Apple Pay' } },
  eur: { 'Налаштування Apple Pay': { title: 'Додати картку до Apple Pay' } },
};

// має збігатися з --card-tilt у CardDesign.css
const TILTED = 'translate(0px, 0px) scale(1) perspective(730px) rotateX(64deg)';
const withAngle = (base, angle) => `${base} rotateY(${angle}deg)`;

// Картка в попапі — та сама розмітка й класи, що й картка на головній
// (CardDesign), тож у нахиленому стані вони збігаються піксель у піксель.
// Шар .cp-fly-wrap повторює .mono-card-wrap (без власної perspective) і стоїть
// у вмісті скролу рівно над карткою з головної; сама картка трансформом
// переїжджає в слот .cp-card-slot і випрямляється.
function layoutFlight(originStack, content, slot, flyWrap, flyStack) {
  const originWrap = originStack.parentElement;
  const w = originWrap.getBoundingClientRect();
  const st = originStack.getBoundingClientRect();
  const c = content.getBoundingClientRect();
  const t = slot.getBoundingClientRect();

  Object.assign(flyWrap.style, {
    left: `${w.left - c.left}px`,
    top: `${w.top - c.top}px`,
    width: `${w.width}px`,
    height: `${w.height}px`,
  });
  Object.assign(flyStack.style, {
    left: `${st.left - w.left}px`,
    top: `${st.top - w.top}px`,
    width: `${st.width}px`,
  });

  // transform-origin картки — center bottom, тож вирівнюємо центр нижнього краю
  const s = t.width / st.width;
  const dx = (t.left + t.width / 2) - (st.left + st.width / 2);
  const dy = t.bottom - st.bottom;
  return `translate(${dx}px, ${dy}px) scale(${s}) perspective(730px) rotateX(0deg)`;
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#fff" />
      <path
        fill="#1d2550"
        d="M8.2 4.6c1.3.3 2 1.1 1.7 2-.3.8-1.4.9-1.6 1.8-.2.9.9 1.4.6 2.4-.3.9-1.7.8-2.4 1.6-.6.7-.2 1.9-.9 2.3-.7-1.2-1.1-2.6-1.1-4.1 0-2.6 1.4-4.9 3.7-6Zm7.3.6c1.6.8 2.9 2.2 3.5 4-.8-.1-1.4.4-1.4 1.1 0 .9 1 1.2 1 2.2 0 .8-.8 1.1-1.1 1.8-.4.9.3 1.9-.4 2.6-.6.6-1.6.4-2-.3-.5-.8 0-1.9-.6-2.6-.6-.7-1.9-.4-2.4-1.2-.5-.8.1-1.9 1-2.2.9-.3 1.9.2 2.4-.5.5-.7-.4-1.6 0-2.4.1-.2.1-.4 0-.5Zm-6 13.3c.5-.6 1.4-.7 2-.2.5.4.5 1.2 1.1 1.4.3.1.6 0 .9 0-1.4.3-2.8.1-4-.5v-.7Z"
      />
    </svg>
  );
}

function PaymentsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M10.5 1.87a3 3 0 0 1 3 0l6.53 3.77a3 3 0 0 1 1.5 2.6v7.53a3 3 0 0 1-1.5 2.6l-6.53 3.76a3 3 0 0 1-3 0l-6.53-3.77a3 3 0 0 1-1.5-2.6V8.24a3 3 0 0 1 1.5-2.6L10.5 1.87ZM12 15.6a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2Z"
      />
    </svg>
  );
}

// Попап будь-якої картки. Для додаткових (з БД) передаємо variant — дизайн
// як на головній (CardDesign.css), title — заголовок, secrets — термін дії
// й CVV з БД, miniIcon — мініатюра в шапці; isMain=false прибирає пункти,
// що стосуються лише основної кредитної картки.
export default function CardPopup({
  originEl, cardNumber, holderName, onClosed,
  variant, title = 'Чорна картка', secrets: cardSecrets, miniIcon, isMain = true,
  dailyLimit, dailySpent = 0, currency = 'UAH', label, onCloseCard,
}) {
  const [closing, setClosing] = useState(false);
  // зворотний бік показано — у плашці під карткою денний ліміт
  const [backShown, setBackShown] = useState(false);
  // підтвердження «Закрити картку?»; closeCardRef — підтвердили, картку
  // закриваємо (видаляємо) вже після того, як попап долетить назад
  const [confirmOpen, setConfirmOpen] = useState(false);
  const closeCardRef = useRef(false);
  // іконку плашки анімуємо лише після першого перевороту, не при відкритті
  const [flipped, setFlipped] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const contentRef = useRef(null);
  const slotRef = useRef(null);
  const flyWrapRef = useRef(null);
  const flyStackRef = useRef(null);
  const cardRef = useRef(null);
  const backdropRef = useRef(null);
  const startRafRef = useRef(0);
  const rootRef = useRef(null);
  const tintRef = useRef(null);
  const numberRef = useRef(null);
  const fullNumberRef = useRef(null);
  const bodyBgRef = useRef(null);
  const timeoutRef = useRef(null);
  const popupBaseRef = useRef(null); // трансформ картки в попапі (без повороту)
  const angleRef = useRef(0);         // кут повороту: 0, ±180, ±360…
  const flippingRef = useRef(false);
  const flipRafRef = useRef(0);
  const pressRef = useRef(null); // { id, x, y, touch, timer, fired }
  const suppressClickRef = useRef(false);
  const [copyToast, setCopyToast] = useState(0); // лічильник — перезапускає анімацію плашки
  const copyToastTimerRef = useRef(null);
  const [secrets] = useState(() => (cardSecrets?.cvv && cardSecrets?.expiry
    ? cardSecrets
    : generateCardSecrets(getStoredChatId() || cardNumber || 'card')));
  const groups = isMain
    ? GROUPS
    : [...GROUPS, CLOSE_CARD_GROUP].map((rows) => rows
      .filter((row) => !MAIN_ONLY_ROWS.includes(row.title))
      .map((row) => ({ ...row, ...ROW_OVERRIDES_BY_VARIANT[variant]?.[row.title] })));
  const bodyBg = BODY_BG_BY_VARIANT[variant] || BODY_BG;
  const limit = Number(dailyLimit) || 0;
  const left = Math.max(0, limit - (Number(dailySpent) || 0));

  const fullNumber = cardNumber || DEFAULT_CARD_NUMBER;
  const numberGroups = fullNumber.match(/.{1,4}/g) || [];

  // Ставить картку в стан «як на головній» (tilted) або «в попапі» і,
  // якщо передано тривалість, анімує перехід туди.
  // Торець (.mono-card-backdrop) летить разом з карткою тим самим
  // трансформом: нахиленим — зсунутий на 4px (видно товщину), у попапі —
  // рівно під карткою (не видно). Як у monobank: при відкритті товщина
  // зникає, поки картка випрямляється, при закритті — з'являється.
  function applyState(target, ms, ease = OPEN_EASE) {
    const card = cardRef.current;
    const backdrop = backdropRef.current;
    const stack = flyStackRef.current;
    const tint = tintRef.current;
    const number = numberRef.current;
    const fullNumberEl = fullNumberRef.current;
    const t = (prop, easing = 'ease') => `${prop} ${ms}ms ${easing}`;
    card.style.transition = ms ? t('transform', ease) : 'none';
    // тінь і торець перемикає клас .face-up — з тією ж тривалістю
    card.style.setProperty('--depth-ms', `${ms}ms`);
    backdrop.style.transition = ms ? `${t('transform', ease)}, ${t('box-shadow')}` : 'none';
    tint.style.transition = ms ? t('opacity') : 'none';
    // у картці з головної номер замаскований, у попапі — повний; кольори
    // цифр/логотипів анімує CSS (.face-up), тут лише перехресне згасання
    // не одночасно, а по черзі: спершу гасне один номер, потім проявляється
    // інший — інакше на півдорозі цифри накладаються
    const up = target !== TILTED;
    const half = Math.round(ms * 0.4);
    const fadeOut = ms ? `opacity ${half}ms ease` : 'none';
    const fadeIn = ms ? `opacity ${half}ms ease ${ms - half}ms` : 'none';
    number.style.transition = up ? fadeOut : fadeIn;
    fullNumberEl.style.transition = up ? fadeIn : fadeOut;
    card.style.transform = withAngle(target, angleRef.current);
    backdrop.style.opacity = '1';
    backdrop.style.transform = `${up ? BACKDROP_HIDDEN : BACKDROP_SHIFT} ${withAngle(target, angleRef.current)}`;
    tint.style.opacity = up ? '0' : '1';
    number.style.opacity = up ? '0' : '1';
    fullNumberEl.style.opacity = up ? '1' : '0';
    stack.classList.toggle('face-up', up);
  }

  function measure() {
    return layoutFlight(originEl, contentRef.current, slotRef.current, flyWrapRef.current, flyStackRef.current);
  }

  // Запуск анімації — лише після того, як попап уже намальовано. Перший кадр
  // попапа важкий (на Android — сотні мс), а CSS-перехід іде за годинником:
  // стартуй він одразу, картка «перестрибнула» б майже в кінець
  function afterPaint(fn) {
    cancelAnimationFrame(startRafRef.current);
    startRafRef.current = requestAnimationFrame(() => {
      startRafRef.current = requestAnimationFrame(fn);
    });
  }

  // Відкриття: стартуємо точно з картки на головній і летимо в слот
  useLayoutEffect(() => {
    // атрибут, а не клас: className кореня перезаписує React
    const show = () => rootRef.current.setAttribute('data-shown', '');
    if (!originEl) {
      afterPaint(show);
      return;
    }
    const popupState = measure();
    popupBaseRef.current = popupState;
    applyState(TILTED, 0);
    afterPaint(() => {
      show();
      applyState(popupState, OPEN_MS);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originEl]);

  // Колір <body> (видно під home-indicator) міняємо плавно разом із фоном
  // попапа — і при закритті повертаємо одразу на старті анімації, а не
  // стрибком після неї
  useEffect(() => {
    const body = document.body.style;
    const original = { color: body.backgroundColor, transition: body.transition };
    bodyBgRef.current = original.color;
    body.transition = 'background-color 0.25s ease';
    body.backgroundColor = bodyBg;
    return () => {
      body.backgroundColor = original.color;
      body.transition = original.transition;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      cancelAnimationFrame(flipRafRef.current);
      cancelAnimationFrame(startRafRef.current);
    };
    // колір фону фіксуємо на час життя попапа
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isBackShown = () => Math.abs(angleRef.current / 180) % 2 === 1;

  // Переворот на 180° у бік dir (+1 — правий край іде від нас, -1 — лівий).
  // Бік міняємо, коли картка проходить ребро (90°). Плашка під карткою
  // міняється одразу на старті — як у monobank.
  // Перспективу обгортки на час перевороту вимикаємо: її центр — над
  // карткою на головній, і поворот виглядав би «знизу». Свою перспективу
  // підставляємо замість perspective() у трансформі попапа — при куті
  // 0/180° вона ні на що не впливає, тож підміна непомітна.
  function flip(dir, onDone, duration = FLIP_MS) {
    if (flippingRef.current) return;
    const card = cardRef.current;
    const base = popupBaseRef.current;
    if (!card || !base) return;
    flippingRef.current = true;
    const from = angleRef.current;
    const to = from + dir * 180;
    const showBack = !isBackShown();
    const w = card.offsetWidth;
    const flipBase = base.replace(/perspective\([^)]*\)/, `perspective(${w * FLIP_PERSPECTIVE}px)`);
    setBackShown(showBack);
    setFlipped(true);
    flyWrapRef.current.style.perspective = 'none';
    card.style.transition = 'none';
    // торець лежить рівно під карткою й не повертається — на час перевороту ховаємо
    backdropRef.current.style.transition = 'none';
    backdropRef.current.style.opacity = '0';
    let swapped = false;
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const angle = from + (to - from) * easeInOutSine(t);
      if (!swapped && Math.abs(angle - from) >= 90) {
        card.classList.toggle('show-back', showBack);
        swapped = true;
      }
      const depth = -w * FLIP_DEPTH * Math.abs(Math.sin((angle * Math.PI) / 180));
      card.style.transform = `${flipBase} translateZ(${depth}px) rotateY(${angle}deg)`;
      if (t < 1) {
        flipRafRef.current = requestAnimationFrame(tick);
        return;
      }
      angleRef.current = to;
      card.style.transform = withAngle(base, to);
      flyWrapRef.current.style.perspective = '';
      backdropRef.current.style.transform = `${BACKDROP_HIDDEN} ${withAngle(base, to)}`;
      backdropRef.current.style.opacity = '1';
      flippingRef.current = false;
      if (onDone) onDone();
    };
    flipRafRef.current = requestAnimationFrame(tick);
  }

  // Копіювання. iOS дозволяє його лише синхронно всередині жесту (touchend),
  // а clipboard API на http:// (локальний dev-сервер) взагалі нема — тож
  // спершу execCommand через виділену textarea, потім clipboard API
  function copyText(text) {
    let ok = false;
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', ''); // без клавіатури
      // 16px — щоб iOS не зумив сторінку на фокусі
      ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;border:0;padding:0;-webkit-user-select:text;user-select:text;';
      document.body.appendChild(ta);
      ta.focus({ preventScroll: true });
      ta.select();
      ta.setSelectionRange(0, text.length);
      ok = document.execCommand('copy');
      ta.blur();
      document.body.removeChild(ta);
    } catch (e) { /* ignore */ }
    if (!ok && navigator.clipboard?.writeText) navigator.clipboard.writeText(text).catch(() => {});
  }

  function showCopyToast() {
    setCopyToast((n) => n + 1);
    clearTimeout(copyToastTimerRef.current);
    copyToastTimerRef.current = setTimeout(() => setCopyToast(0), COPY_TOAST_MS);
  }
  useEffect(() => () => clearTimeout(copyToastTimerRef.current), []);

  function cancelPress() {
    if (pressRef.current) clearTimeout(pressRef.current.timer);
    pressRef.current = null;
  }

  function handleCardPointerDown(e) {
    if (closing || flippingRef.current || (e.pointerType === 'mouse' && e.button !== 0)) return;
    cancelPress();
    const press = { id: e.pointerId, x: e.clientX, y: e.clientY, touch: e.pointerType !== 'mouse', fired: false };
    // плашка — одразу, поки палець ще тримає; сам номер копіюємо при відпусканні
    press.timer = setTimeout(() => { press.fired = true; showCopyToast(); }, LONG_PRESS_MS);
    pressRef.current = press;
  }

  function handleCardPointerMove(e) {
    const press = pressRef.current;
    if (!press || press.id !== e.pointerId || press.fired) return;
    if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > LONG_PRESS_MOVE) cancelPress();
  }

  function finishPress() {
    const press = pressRef.current;
    cancelPress();
    if (!press || !press.fired) return;
    // після затискання тап (переворот) не спрацьовує
    suppressClickRef.current = true;
    setTimeout(() => { suppressClickRef.current = false; }, 400);
    copyText(fullNumber);
  }

  // Миша — копіюємо на pointerup; дотик — на touchend (його iOS вважає жестом)
  function handleCardPointerUp(e) {
    const press = pressRef.current;
    if (!press || press.id !== e.pointerId || press.touch) return;
    finishPress();
  }

  function handleCardPointerCancel() {
    // iOS після довгого утримання інколи шле pointercancel — touchend усе одно прийде
    if (pressRef.current?.touch && pressRef.current.fired) return;
    cancelPress();
  }

  // Тап по картці: перевертаємо в бік тієї половини, по якій натиснули
  function handleCardClick(e) {
    if (closing) return;
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    const r = e.currentTarget.getBoundingClientRect();
    flip(e.clientX >= r.left + r.width / 2 ? 1 : -1);
  }

  // Закриття: перераховуємо позицію з урахуванням прокрутки (картка
  // лишається на місці), потім летимо назад і нахиляємось. Наприкінці
  // картка збігається з карткою на головній, тож підміна непомітна.
  function handleClose() {
    if (closing || flippingRef.current) return;
    // перевернуту картку спершу повертаємо лицем, потім летимо назад
    if (isBackShown()) {
      flip(angleRef.current > 0 ? 1 : -1, handleClose, FLIP_CLOSE_MS);
      return;
    }
    setClosing(true);
    document.body.style.transition = `background-color ${CLOSE_MS}ms ease`;
    document.body.style.backgroundColor = bodyBgRef.current;
    if (originEl) {
      const popupState = measure();
      popupBaseRef.current = popupState;
      applyState(popupState, 0);
    }
    // як і при відкритті — стартуємо після кадру з перемальованим попапом
    afterPaint(() => {
      if (originEl) applyState(TILTED, CLOSE_MS, CLOSE_EASE);
      timeoutRef.current = setTimeout(() => {
        onClosed();
        if (closeCardRef.current && onCloseCard) onCloseCard();
        // із запасом на кадр: таймер може спрацювати раніше за останній кадр
        // переходу, і картка підмінилася б ще не долетівши — стрибок
      }, CLOSE_MS + 50);
    });
  }

  function confirmCloseCard() {
    setConfirmOpen(false);
    closeCardRef.current = true;
    handleClose();
  }

  // Коли велика картка сховалась під шапку — показуємо мініатюру в шапці
  function handleScroll(e) {
    const slot = slotRef.current;
    if (!slot) return;
    const threshold = slot.offsetTop + slot.offsetHeight * 0.6;
    setCollapsed(e.currentTarget.scrollTop > threshold);
  }

  return createPortal((
    <div ref={rootRef} className={`cp-root ${variant ? `cp-root--${variant}` : ''} ${closing ? 'closing' : ''}`}>
      <div className="cp-bg" />

      {copyToast > 0 && (
        <div className="cp-copy-toast" key={copyToast}>
          <svg className="cp-copy-toast-icon" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
            <rect x="2" y="2" width="14" height="14" rx="3.5" fillOpacity="0.85" />
            <rect x="7.5" y="7.5" width="14.5" height="14.5" rx="3.5" stroke="#00b956" strokeWidth="1.6" />
          </svg>
          Номер картки скопійовано
        </div>
      )}

      <div className={`cp-topbar ${collapsed ? 'collapsed' : ''}`}>
        <button type="button" className="cp-close" onClick={handleClose} aria-label="Закрити">
          <svg viewBox="0 0 24 24" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4l16 16M20 4L4 20" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
        {miniIcon
          ? <img className="cp-mini-card cp-mini-card--icon" src={miniIcon} alt="" />
          : variant
            ? <div className="cp-mini-card cp-mini-card--placeholder" />
            : <img className="cp-mini-card" src="/mini-mono.svg" alt="" />}
      </div>

      <div className="cp-scroll" onScroll={handleScroll}>
        <div ref={contentRef} className="cp-content">
        <div ref={slotRef} className="cp-card-slot" />

        <div ref={flyWrapRef} className="cp-fly-wrap">
          <div ref={flyStackRef} className={`mono-card-stack cp-fly-stack ${variant ? `variant-${variant}` : ''}`}>
            <div ref={backdropRef} className="mono-card-backdrop" />
            <div
              ref={cardRef}
              className="mono-card cp-fly-card"
              onClick={handleCardClick}
              onPointerDown={handleCardPointerDown}
              onPointerMove={handleCardPointerMove}
              onPointerUp={handleCardPointerUp}
              onPointerCancel={handleCardPointerCancel}
              onTouchEnd={() => { if (pressRef.current?.touch) finishPress(); }}
              onTouchCancel={cancelPress}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="cp-card-shadow-up" />
              <CardDepth />
              <div ref={tintRef} className="cp-card-tint" />
              <div className="mono-card-brand"><MonoLogo /></div>
              {label && <div className="mono-card-label">{label}</div>}
              <div ref={numberRef} className="mono-card-number">
                <span>{fullNumber.slice(0, 4)}</span>
                <span>****</span>
                <span>****</span>
                <span>{fullNumber.slice(-4)}</span>
              </div>
              <div ref={fullNumberRef} className="mono-card-number cp-full-number">
                {numberGroups.map((group, i) => <span key={i}>{group}</span>)}
              </div>
              {holderName && <div className="mono-card-holder cp-holder">{holderName}</div>}
              <div className="mono-card-visa"><VisaLogo /></div>

              {/* Зворотний бік: магнітна смуга, термін дії, CVV */}
              <div className="cp-card-back">
                <div className="cp-card-stripe" />
                <div className="cp-card-expiry">{secrets.expiry}</div>
                <div className="cp-card-cvv">
                  <div className="cp-card-cvv-dots" />
                  <span className="cp-card-cvv-value">{secrets.cvv}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="cp-body">
          <div className="cp-group cp-group--dark">
            {backShown && limit > 0 ? (
              <div className="cp-row">
                <div className="cp-row-icon cp-row-icon--flip" key="limit"><GlobeIcon /></div>
                <div className="cp-row-text cp-limit">
                  <p className="cp-row-title cp-limit-title">Ліміт оплат за день</p>
                  <div className="cp-limit-bar">
                    <div className="cp-limit-fill" style={{ width: `${(left / limit) * 100}%` }} />
                  </div>
                  <p className="cp-row-sub">
                    Залишилося {formatMoney(left, currency, 0)} з {formatMoney(limit, currency, 0)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="cp-row">
                <div className={`cp-row-icon ${flipped && limit > 0 ? 'cp-row-icon--flip' : ''}`} key="payments"><PaymentsIcon /></div>
                <div className="cp-row-text">
                  <p className="cp-row-title">Налаштування оплат</p>
                  <p className="cp-row-sub">Додаткові перевірки</p>
                </div>
              </div>
            )}
          </div>

          <h2 className="cp-heading">{title}</h2>

          {groups.map((rows, gi) => (
            <div className="cp-group" key={gi}>
              {rows.map((row) => (
                <div
                  className="cp-row"
                  key={row.title}
                  onClick={row.action === 'close-card' ? () => setConfirmOpen(true) : undefined}
                >
                  <div className="cp-row-icon">
                    <img src={row.icon} alt="" />
                  </div>
                  <div className="cp-row-text">
                    <p className="cp-row-title">{row.title}</p>
                    {row.sub && <p className="cp-row-sub">{row.sub}</p>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        </div>
      </div>

      {confirmOpen && (
        <div className="cp-alert-overlay">
          <div className="cp-alert" role="alertdialog">
            <div className="cp-alert-text">
              <p className="cp-alert-title">Ви впевнені, що хочете<br />закрити картку?</p>
              <p className="cp-alert-message">Ви зможете відкрити нову картку<br />в будь-який момент</p>
            </div>
            <button type="button" className="cp-alert-btn cp-alert-btn--danger" onClick={confirmCloseCard}>
              Закрити картку
            </button>
            <button type="button" className="cp-alert-btn cp-alert-btn--cancel" onClick={() => setConfirmOpen(false)}>
              Скасувати
            </button>
          </div>
        </div>
      )}
    </div>
  ), document.body);
}
